import { useState } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Building2, User, Mail, CheckCircle, Loader2 } from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';
import Seo from '../components/Seo';
import { pickMeta } from '../seoMeta';

function Register({ setUser }) {
  const { t, i18n } = useTranslation();
  const { lang } = useParams();
  const currentLang = lang || i18n.language || 'ru';
  
  const [step, setStep] = useState(0); // 0 = type, 1 = email, 2 = code, 3 = details
  const [emailVerified, setEmailVerified] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [sendingCode, setSendingCode] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    phone: '',
    address: '',
    role: '',
    company_name: '',
    tax_id: '',
    contact_person: '',
    first_name: '',
    last_name: ''
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSelectRole = (role) => {
    setFormData({ ...formData, role });
    setStep(1);
  };

  const handleSendCode = async () => {
    if (!formData.email) {
      toast.error(t('auth.enterEmail') || 'Введите email');
      return;
    }
    
    setSendingCode(true);
    try {
      await axios.post(`${API}/auth/send-verification-code`, { email: formData.email });
      toast.success(t('auth.codeSent') || 'Код отправлен на вашу почту');
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth.sendCodeError') || 'Ошибка отправки кода');
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error(t('auth.enterCode') || 'Введите 6-значный код');
      return;
    }
    
    setVerifyingCode(true);
    try {
      await axios.post(`${API}/auth/verify-code`, { 
        email: formData.email, 
        code: verificationCode 
      });
      toast.success(t('auth.codeVerified') || 'Email подтверждён');
      setEmailVerified(true);
      setStep(3);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth.invalidCode') || 'Неверный код');
    } finally {
      setVerifyingCode(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!emailVerified) {
      toast.error(t('auth.verifyEmailFirst') || 'Сначала подтвердите email');
      return;
    }

    // Validation
    if (formData.role === 'B2C') {
      if (!formData.first_name || !formData.last_name) {
        toast.error('Введите имя и фамилию');
        return;
      }
    } else {
      if (!formData.company_name || !formData.tax_id || !formData.contact_person) {
        toast.error('Заполните все обязательные поля');
        return;
      }
      // ETAP-1: БИН validation (12 digits) — prevents duplicate Bitrix companies
      const bin = (formData.tax_id || '').replace(/\s+/g, '');
      if (!/^\d{12}$/.test(bin)) {
        toast.error('БИН должен состоять ровно из 12 цифр');
        return;
      }
      formData.tax_id = bin;
    }
    
    setLoading(true);

    try {
      // Prepare data - combine first_name and last_name for full_name
      const submitData = {
        ...formData,
        full_name: formData.role === 'B2C' ? `${formData.first_name} ${formData.last_name}`.trim() : ''
      };

      const response = await axios.post(`${API}/auth/register`, submitData);
      localStorage.setItem('token', response.data.token);
      setUser(response.data.user);
      toast.success(t('auth.registerSuccess'));
      navigate(`/${currentLang}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth.registerError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {(() => { const m = pickMeta('register', currentLang); return <Seo lang={currentLang} path="register" title={m.title} description={m.description} />; })()}
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to={`/${currentLang}`} className="flex items-center">
            <img 
              src="/logo.jpg" 
              alt="Ainanan" 
              className="h-10 object-contain"
            />
          </Link>
          <LanguageSwitcher />
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center p-4 py-8">
        <div className="w-full max-w-lg">
          <Link to={`/${currentLang}`} className="inline-flex items-center text-stone-600 hover:text-stone-800 mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('auth.backToCatalog')}
          </Link>

          <div className="bg-white rounded-xl shadow-sm border p-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('auth.registerTitle')}</h1>
            <p className="text-gray-500 mb-6">{t('auth.registerSubtitle')}</p>

            {/* Progress Steps */}
            {step > 0 && (
              <div className="flex items-center justify-center mb-8">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full ${step >= 1 ? 'bg-emerald-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                  {step > 1 ? <CheckCircle className="w-5 h-5" /> : '1'}
                </div>
                <div className={`w-12 h-1 ${step >= 2 ? 'bg-emerald-600' : 'bg-gray-200'}`} />
                <div className={`flex items-center justify-center w-8 h-8 rounded-full ${step >= 2 ? 'bg-emerald-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                  {step > 2 ? <CheckCircle className="w-5 h-5" /> : '2'}
                </div>
                <div className={`w-12 h-1 ${step >= 3 ? 'bg-emerald-600' : 'bg-gray-200'}`} />
                <div className={`flex items-center justify-center w-8 h-8 rounded-full ${step >= 3 ? 'bg-emerald-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                  3
                </div>
              </div>
            )}

            {/* Step 0: Select Type */}
            {step === 0 && (
              <div className="space-y-6">
                <div className="text-center mb-4">
                  <h3 className="font-semibold text-gray-900 text-lg">{t('auth.selectAccountType') || 'Выберите тип аккаунта'}</h3>
                  <p className="text-sm text-gray-500">{t('auth.selectAccountTypeDesc') || 'Это поможет нам предоставить вам лучший сервис'}</p>
                </div>
                
                <div className="grid gap-4">
                  <button
                    type="button"
                    onClick={() => handleSelectRole('B2C')}
                    className="flex items-center gap-4 p-6 rounded-xl border-2 border-gray-200 hover:border-emerald-500 hover:bg-emerald-50 transition-all text-left group"
                    data-testid="select-b2c"
                  >
                    <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
                      <User className="w-7 h-7 text-emerald-700" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 text-lg">{t('auth.individual') || 'Частное лицо'}</h4>
                      <p className="text-sm text-gray-500">{t('auth.individualDesc') || 'Для личных покупок и заказов'}</p>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleSelectRole('B2B')}
                    className="flex items-center gap-4 p-6 rounded-xl border-2 border-gray-200 hover:border-emerald-500 hover:bg-emerald-50 transition-all text-left group"
                    data-testid="select-b2b"
                  >
                    <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
                      <Building2 className="w-7 h-7 text-emerald-700" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 text-lg">{t('auth.company') || 'Компания'}</h4>
                      <p className="text-sm text-gray-500">{t('auth.companyDesc') || 'Для бизнеса: кафе, рестораны, отели'}</p>
                    </div>
                  </button>
                </div>
              </div>
            )}

            {/* Step 1: Email */}
            {step === 1 && (
              <div className="space-y-4">
                <div className="text-center mb-4">
                  <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm mb-3">
                    {formData.role === 'B2C' ? <User className="w-4 h-4" /> : <Building2 className="w-4 h-4" />}
                    {formData.role === 'B2C' ? t('auth.individual') : t('auth.company')}
                  </div>
                  <Mail className="w-12 h-12 text-emerald-600 mx-auto mb-2" />
                  <h3 className="font-semibold text-gray-900">{t('auth.step1Title') || 'Введите ваш email'}</h3>
                  <p className="text-sm text-gray-500">{t('auth.step1Desc') || 'Мы отправим код подтверждения'}</p>
                </div>
                
                <div>
                  <Label htmlFor="email" className="text-gray-700">{t('auth.email')} *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    placeholder="your@email.com"
                    className="mt-1"
                    data-testid="register-email-input"
                  />
                </div>

                <Button
                  onClick={handleSendCode}
                  disabled={sendingCode || !formData.email}
                  className="w-full bg-emerald-700 hover:bg-emerald-800 text-white font-medium py-2.5"
                  data-testid="send-code-button"
                >
                  {sendingCode ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('auth.sending') || 'Отправка...'}
                    </>
                  ) : (
                    t('auth.sendCode') || 'Отправить код'
                  )}
                </Button>

                <button
                  type="button"
                  onClick={() => setStep(0)}
                  className="w-full text-sm text-gray-500 hover:text-gray-700"
                >
                  {t('auth.changeType') || '← Изменить тип аккаунта'}
                </button>
              </div>
            )}

            {/* Step 2: Verification Code */}
            {step === 2 && (
              <div className="space-y-4">
                <div className="text-center mb-4">
                  <CheckCircle className="w-12 h-12 text-emerald-600 mx-auto mb-2" />
                  <h3 className="font-semibold text-gray-900">{t('auth.step2Title') || 'Введите код'}</h3>
                  <p className="text-sm text-gray-500">
                    {t('auth.step2Desc') || 'Код отправлен на'} <span className="font-medium">{formData.email}</span>
                  </p>
                </div>

                <div>
                  <Label htmlFor="code" className="text-gray-700">{t('auth.verificationCode') || 'Код подтверждения'}</Label>
                  <Input
                    id="code"
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className="mt-1 text-center text-2xl tracking-widest font-mono"
                    maxLength={6}
                    data-testid="verification-code-input"
                  />
                </div>

                <Button
                  onClick={handleVerifyCode}
                  disabled={verifyingCode || verificationCode.length !== 6}
                  className="w-full bg-emerald-700 hover:bg-emerald-800 text-white font-medium py-2.5"
                  data-testid="verify-code-button"
                >
                  {verifyingCode ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('auth.verifying') || 'Проверка...'}
                    </>
                  ) : (
                    t('auth.verifyCode') || 'Подтвердить'
                  )}
                </Button>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={handleSendCode}
                    disabled={sendingCode}
                    className="text-sm text-emerald-700 hover:text-emerald-800"
                  >
                    {t('auth.resendCode') || 'Отправить код повторно'}
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="w-full text-sm text-gray-500 hover:text-gray-700"
                >
                  {t('auth.changeEmail') || '← Изменить email'}
                </button>
              </div>
            )}

            {/* Step 3: Registration Details */}
            {step === 3 && (
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Email verified badge */}
                <div className="flex items-center gap-2 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                  <span className="text-sm text-emerald-700">
                    {formData.email} — {t('auth.emailVerified') || 'подтверждён'}
                  </span>
                  <span className="ml-auto text-xs px-2 py-1 bg-emerald-100 text-emerald-700 rounded">
                    {formData.role === 'B2C' ? t('auth.individual') : t('auth.company')}
                  </span>
                </div>

                {/* Password */}
                <div>
                  <Label htmlFor="password" className="text-gray-700">{t('auth.password')} *</Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    placeholder="Мин. 8 символов, буква и цифра"
                    className="mt-1"
                    minLength={8}
                    data-testid="register-password-input"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Не менее 8 символов, должен содержать хотя бы одну букву и одну цифру
                  </p>
                </div>

                {/* B2C Fields */}
                {formData.role === 'B2C' && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="first_name" className="text-gray-700">{t('auth.firstName') || 'Имя'} *</Label>
                        <Input
                          id="first_name"
                          type="text"
                          value={formData.first_name}
                          onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                          required
                          placeholder="Иван"
                          className="mt-1"
                          data-testid="register-firstname-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="last_name" className="text-gray-700">{t('auth.lastName') || 'Фамилия'} *</Label>
                        <Input
                          id="last_name"
                          type="text"
                          value={formData.last_name}
                          onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                          required
                          placeholder="Иванов"
                          className="mt-1"
                          data-testid="register-lastname-input"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="phone" className="text-gray-700">
                        {t('auth.phone')} <span className="text-gray-400 text-sm">({t('common.optional') || 'необязательно'})</span>
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="+7 777 123 4567"
                        className="mt-1"
                        data-testid="register-phone-input"
                      />
                    </div>

                    <div>
                      <Label htmlFor="address" className="text-gray-700">
                        {t('auth.deliveryAddress')} <span className="text-gray-400 text-sm">({t('common.optional') || 'необязательно'})</span>
                      </Label>
                      <Input
                        id="address"
                        type="text"
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        placeholder="Город, улица, дом"
                        className="mt-1"
                        data-testid="register-address-input"
                      />
                    </div>
                  </>
                )}

                {/* B2B Fields */}
                {formData.role === 'B2B' && (
                  <>
                    <div>
                      <Label htmlFor="company_name" className="text-gray-700">{t('auth.companyName')} *</Label>
                      <Input
                        id="company_name"
                        type="text"
                        value={formData.company_name}
                        onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                        required
                        placeholder="ТОО Компания"
                        className="mt-1"
                        data-testid="register-company-input"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="tax_id" className="text-gray-700">{t('auth.taxId')} * (12 цифр)</Label>
                        <Input
                          id="tax_id"
                          type="text"
                          inputMode="numeric"
                          pattern="\d{12}"
                          maxLength={12}
                          value={formData.tax_id}
                          onChange={(e) => setFormData({ ...formData, tax_id: e.target.value.replace(/\D/g, '').slice(0, 12) })}
                          required
                          placeholder="123456789012"
                          className="mt-1"
                          data-testid="register-taxid-input"
                        />
                      </div>

                      <div>
                        <Label htmlFor="contact_person" className="text-gray-700">{t('auth.contactPerson')} *</Label>
                        <Input
                          id="contact_person"
                          type="text"
                          value={formData.contact_person}
                          onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                          required
                          placeholder="Иван Иванов"
                          className="mt-1"
                          data-testid="register-contact-input"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="phone" className="text-gray-700">
                        {t('auth.phone')} <span className="text-gray-400 text-sm">({t('common.optional') || 'необязательно'})</span>
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="+7 777 123 4567"
                        className="mt-1"
                        data-testid="register-phone-input"
                      />
                    </div>

                    <div>
                      <Label htmlFor="address" className="text-gray-700">
                        {t('auth.deliveryAddress')} <span className="text-gray-400 text-sm">({t('common.optional') || 'необязательно'})</span>
                      </Label>
                      <Input
                        id="address"
                        type="text"
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        placeholder="Город, улица, дом"
                        className="mt-1"
                        data-testid="register-address-input"
                      />
                    </div>
                  </>
                )}

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-emerald-700 hover:bg-emerald-800 text-white font-medium py-2.5"
                  data-testid="register-submit-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('auth.registering')}
                    </>
                  ) : (
                    t('auth.registerButton')
                  )}
                </Button>
              </form>
            )}

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                {t('auth.hasAccount')}{' '}
                <Link to={`/${currentLang}/login`} className="font-medium text-emerald-700 hover:text-emerald-800" data-testid="login-link">
                  {t('auth.loginLink')}
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t py-4">
        <div className="max-w-7xl mx-auto px-4 text-center text-sm text-gray-500">
          © {new Date().getFullYear()} Ainanan. {t('common.allRights')}.
        </div>
      </footer>
    </div>
  );
}

export default Register;
